import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { WORD_CARD_KIND_LIST } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { createUserWordReadingCards } from "@-/dev.improve.haniverse.lib.cards.user-card.create";
import {
  createUserWordCard,
  findEnrolledWordPinyin,
} from "@-/dev.improve.haniverse.lib.cards.user-card.word.create";
import { logger } from "@-/dev.improve.haniverse.lib.logger";

/**
 * Add a word to the deck, creating one card per {@link WORD_CARD_KIND_LIST} kind.
 *
 * A card is identified by `(word, pinyin, kind)`. A word already in the deck keeps the reading it
 * was enrolled under (`findEnrolledWordPinyin`); only a word never added takes its primary
 * dictionary reading (`word_definition`, lowest `index`) — a word tile in Browse has no reading
 * attached. Re-deriving it every time would let a static rebuild that reorders definitions enroll a
 * duplicate set of cards with separate schedules.
 *
 * ALL THREE kinds are authored up front, and which of them actually enters the review rotation is
 * decided later by `isCardContentKindReviewEligible` against the user's per-kind settings. That
 * mirrors how grapheme pronunciation cards already work (`isPinyinFlashcardsEnabled` gates review,
 * not authoring), and it means enabling a drill later surfaces the user's existing vocabulary
 * immediately instead of only words added after the toggle.
 *
 * Returns the `characters-in-context` card's id — the kind that existed before the split, so
 * existing callers keep their meaning — or `null` when the word has no dictionary reading, since
 * there would be no pinyin to prompt with. Callers surface that as "couldn't add" rather than
 * creating an unusable card.
 *
 * Adding a word also introduces the non-main character readings it uses (大夫 → 大 `dai4`): a
 * character only brings its main reading into the deck, so a secondary reading is learned with
 * the first word that needs it.
 */
export function useCreateUserWordCard() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);

  /** The word's primary dictionary reading, for a word the deck has never held. */
  async function readDictionaryPinyin(word: string): Promise<string | null> {
    const wordRow = await staticDb(import.meta.location).query.word.findFirst({
      columns: { id: true },
      where: { word: { eq: word } },
    });
    if (wordRow === undefined) {
      // Log rather than fail silently. The selection overlay dismisses on a resolved mutation
      // regardless of the value, so a bare `return null` is INDISTINGUISHABLE from success in
      // the UI — the card just never appears, with nothing anywhere to explain why.
      logger(import.meta.location).warn(
        `[createUserWordCard] no \`word\` row for "${word}"; cannot create a word card`,
      );
      return null;
    }

    const definition = await staticDb(import.meta.location).query.wordDefinition.findFirst({
      columns: { pinyin: true },
      orderBy: { index: "asc" },
      where: { wordId: { eq: wordRow.id } },
    });
    // `word_definition.pinyin` is a `toneNumbersPinyinText` column, so its read value is already
    // normalized to the canonical tone-number form.
    const pinyin = definition?.pinyin;
    if (pinyin === undefined || pinyin === null || pinyin.length === 0) {
      logger(import.meta.location).warn(
        `[createUserWordCard] word "${word}" (id ${String(wordRow.id)}) has no definition pinyin; ` +
          `cannot create a word card (its prompt would have nothing to ask)`,
      );
      return null;
    }
    return pinyin;
  }

  return useMutation({
    mutationFn: async ({
      startedStudyingAt,
      word,
    }: {
      startedStudyingAt?: string;
      word: string;
    }): Promise<{ userCardId: string } | null> => {
      const pinyin =
        (await findEnrolledWordPinyin({ actorDb, word })) ?? (await readDictionaryPinyin(word));
      if (pinyin === null) {
        return null;
      }

      // Sequential rather than `Promise.all`: each call opens its own transaction against the same
      // actor DB, and the three share a `user_card` insert path — serializing keeps them from
      // contending on the write lock for no benefit (three inserts is not a throughput problem).
      let primary: { userCardId: string } | null = null;
      for (const kind of WORD_CARD_KIND_LIST) {
        const created = await createUserWordCard({
          actorDb,
          kind,
          pinyin,
          startedStudyingAt,
          word,
        });
        if (kind === "characters-in-context") {
          primary = created;
        }
      }
      await createUserWordReadingCards({ actorDb, contentDb, pinyin, startedStudyingAt, word });
      return primary;
    },
    mutationKey: ["createUserWordCard"],
  });
}
